import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import NewClientForm from '../components/NewClientForm';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Loader2, Search, AlertTriangle, CheckCircle, Trash2, Zap, Flame, UserCheck, XCircle, UserPlus, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LUCE e GAS → Fatture estrazione (backend: routes/luce_gas.py, estrazione con l'AI locale su
// xieai). Ogni bolletta: dati estratti + avvisi (campi da verificare) + abbinamento cliente/utenza.
const GRUPPI = [
  ['Bolletta', [
    ['tipo_utenza', 'Tipo'], ['fornitore', 'Fornitore'], ['numero_fattura', 'N. fattura'], ['data_emissione', 'Emissione', 'date'],
    ['periodo_da', 'Periodo dal', 'date'], ['periodo_a', 'Periodo al', 'date'], ['scadenza_pagamento', 'Scadenza pagamento', 'date'],
    ['importo_totale', 'Importo totale €', 'num'], ['modalita_pagamento', 'Pagamento'], ['iban', 'IBAN'],
  ]],
  ['Intestatario', [
    ['ragione_sociale', 'Ragione sociale'], ['partita_iva', 'P.IVA'], ['cognome', 'Cognome'], ['nome', 'Nome'],
    ['codice_fiscale', 'Codice fiscale'], ['indirizzo_fatturazione', 'Indirizzo fatturazione', 'largo'],
  ]],
  ['Fornitura', [
    ['codice_cliente', 'Codice cliente'], ['pod', 'POD'], ['pdr', 'PDR'], ['indirizzo_fornitura', 'Indirizzo fornitura', 'largo'],
    ['tipologia_uso', 'Uso'], ['potenza_impegnata_kw', 'Potenza impegnata kW', 'num'], ['potenza_disponibile_kw', 'Potenza disponibile kW', 'num'],
    ['classe_misuratore', 'Classe misuratore'], ['matricola_contatore', 'Matricola'],
  ]],
  ['Consumi', [
    ['consumo_periodo', 'Consumo periodo', 'num'], ['consumo_annuo', 'Consumo annuo', 'num'],
    // F1/F2/F3 su una riga (4° elemento "terzo"): prima quelli della bolletta, poi gli annui
    ['consumo_f1', 'F1 kWh (bolletta)', 'num', 'terzo'], ['consumo_f2', 'F2 kWh (bolletta)', 'num', 'terzo'], ['consumo_f3', 'F3 kWh (bolletta)', 'num', 'terzo'],
    ['consumo_annuo_f1', 'F1 kWh annuo', 'num', 'terzo'], ['consumo_annuo_f2', 'F2 kWh annuo', 'num', 'terzo'], ['consumo_annuo_f3', 'F3 kWh annuo', 'num', 'terzo'],
  ]],
  ['Offerta e prezzi', [
    ['nome_offerta', 'Offerta'], ['codice_offerta', 'Codice offerta'], ['tipo_prezzo', 'Tipo prezzo'],
    ['prezzo_fatturato', 'Prezzo fatturato €/u', 'num'], ['prezzo_energia', 'Prezzo venditore €/u', 'num'],
    ['indice_riferimento', 'Indice'], ['indice_base', 'Valore base indice', 'num'], ['spread', 'Spread €/u', 'num'],
    ['indice_f1', 'Indice F1 €/kWh', 'num'], ['indice_f2', 'Indice F2 €/kWh', 'num'], ['indice_f3', 'Indice F3 €/kWh', 'num'],
    ['perdite_rete', 'Perdite di rete (0,10 = 10%)', 'num'], ['dispacciamento', 'Dispacciamento €/kWh', 'num'], ['sbilanciamento', 'Sbilanciamento €/kWh', 'num'],
    ['quota_fissa_mensile', 'Quota fissa €/mese', 'num'], ['quota_potenza', 'Quota potenza €/kW/mese', 'num'],
    ['data_inizio_fornitura', 'Inizio fornitura', 'date'], ['scadenza_condizioni', 'Scadenza condizioni', 'date'],
  ]],
];

const ETICHETTE = Object.fromEntries(GRUPPI.flatMap(([, campi]) => campi.map(([k, l]) => [k, l])));

// bollette di più mesi: questi valori si mostrano mese per mese (sezione "Valori mese per mese"), non una volta sola
const CAMPI_MENSILI = ['indice_f1', 'indice_f2', 'indice_f3', 'dispacciamento', 'sbilanciamento'];
const MESI_NOMI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const CAMPI_MESE = [
  ['consumo_f1', 'Consumo F1 kWh'], ['consumo_f2', 'Consumo F2 kWh'], ['consumo_f3', 'Consumo F3 kWh'],
  ['indice_f1', 'PUN F1 €/kWh'], ['indice_f2', 'PUN F2 €/kWh'], ['indice_f3', 'PUN F3 €/kWh'],
  ['prezzo_fisso_f1', 'Prezzo fisso F1 €/kWh'], ['prezzo_fisso_f2', 'Prezzo fisso F2 €/kWh'], ['prezzo_fisso_f3', 'Prezzo fisso F3 €/kWh'],
  ['spread_f1', 'Spread F1 €/kWh'], ['spread_f2', 'Spread F2 €/kWh'], ['spread_f3', 'Spread F3 €/kWh'],
  ['dispacciamento', 'Dispacciamento €/kWh'], ['sbilanciamento', 'Sbilanciamento €/kWh'], ['capacita', 'Capacity market €/kWh'],
  ['capacita_picco', 'Capacità picco €/kWh'], ['capacita_fuori_picco', 'Capacità fuori picco €/kWh'],
];

const STATI = {
  in_coda: { label: 'In coda', classe: 'bg-slate-100 text-slate-700' },
  in_lettura: { label: 'Lettura in corso…', classe: 'bg-yellow-100 text-yellow-800' },
  errore: { label: 'Errore', classe: 'bg-red-100 text-red-700' },
  da_verificare: { label: 'Da verificare', classe: 'bg-amber-100 text-amber-800' },
  da_abbinare: { label: 'Da abbinare', classe: 'bg-sky-100 text-sky-800' },
  abbinata: { label: 'Abbinata', classe: 'bg-emerald-100 text-emerald-800' },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '-');
const formatCurrency = (v) => (v === null || v === undefined || v === '' ? '-' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v)));
const formatNum = (v) => (v === null || v === undefined || v === '' ? '-' : new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(Number(v)));
const intestatario = (b) => b.client_name || b.dati.ragione_sociale || `${b.dati.cognome || ''} ${b.dati.nome || ''}`.trim() || b.file_name || '-';
const inLavorazione = (b) => ['in_coda', 'in_lettura', 'errore'].includes(b.stato);
// campi che restano in minuscolo nei dati (valori interni) e campi non di testo
const NON_MAIUSCOLO = new Set(['tipo_utenza', 'tipo_prezzo']);

const FattureEstrazione = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipo, setTipo] = useState('');
  const [stato, setStato] = useState('');
  const [ordine, setOrdine] = useState('desc'); // data/ora di caricamento: desc = più recenti in cima
  const [invio, setInvio] = useState(null); // { fatti, totale, errori } durante il caricamento dei file
  const [dettaglio, setDettaglio] = useState(null); // { bolletta, dati, confermati:Set, fileUrl, isPdf }
  const [salvando, setSalvando] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selezionate, setSelezionate] = useState(new Set());
  const [anteprima, setAnteprima] = useState(null); // { bolletta, cliente, beni } oppure { bolletta, nuovo: true }
  const navigate = useNavigate();

  // Clic sul nome: anteprima dell'anagrafica se il cliente esiste, altrimenti proposta di registrarlo
  const apriCliente = async (b) => {
    if (!b.client_id) { setAnteprima({ bolletta: b, nuovo: true }); return; }
    setAnteprima({ bolletta: b, caricamento: true });
    try {
      const [c, beni] = await Promise.all([
        axios.get(`${API}/clients/${b.client_id}`),
        axios.get(`${API}/assets`, { params: { client_id: b.client_id } }),
      ]);
      setAnteprima({ bolletta: b, cliente: c.data, beni: (beni.data || []).filter((x) => x.asset_type === 'utenze') });
    } catch (_) {
      toast.error('Impossibile aprire l\'anagrafica del cliente');
      setAnteprima(null);
    }
  };

  // dati della bolletta per precompilare la registrazione del cliente
  const prefillDaBolletta = (d) => ({
    is_company: !!d.intestatario_azienda,
    company_name: d.intestatario_azienda ? d.ragione_sociale : '',
    vat_number: d.partita_iva || '',
    last_name: d.cognome || '',
    first_name: d.nome || '',
    fiscal_code: d.codice_fiscale || '',
  });

  const clienteCreato = async (c) => {
    try {
      await axios.post(`${API}/luce-gas/bollette/${anteprima.bolletta.id}/abbina`, { client_id: c.id, crea_utenza: true });
      toast.success('Cliente registrato e bolletta abbinata (utenza aggiunta)');
    } catch (_) {
      toast.error('Cliente registrato, ma l\'abbinamento della bolletta non è riuscito');
    }
    setClients((prev) => [c, ...prev]);
    fetchItems();
    apriCliente({ ...anteprima.bolletta, client_id: c.id });
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/luce-gas/bollette`, { params: { search, tipo, stato, ordine, page_size: 200 } });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Errore nel caricamento delle bollette');
    } finally {
      setLoading(false);
    }
  }, [search, tipo, stato, ordine]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  // selezione valida solo per le righe visibili
  useEffect(() => { setSelezionate((s) => new Set([...s].filter((id) => items.some((b) => b.id === id)))); }, [items]);

  const eliminaBollette = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(ids.length === 1 ? 'Eliminare questa bolletta dall\'archivio?' : `Eliminare ${ids.length} bollette dall'archivio?`)) return;
    let ok = 0;
    for (const id of ids) {
      try { await axios.delete(`${API}/luce-gas/bollette/${id}`); ok += 1; } catch (_) { /* conteggio sotto */ }
    }
    if (ok < ids.length) toast.error(`Eliminate ${ok} su ${ids.length}`);
    else toast.success(ok === 1 ? 'Bolletta eliminata' : `${ok} bollette eliminate`);
    setSelezionate(new Set());
    fetchItems();
  };

  const tutteSelezionate = items.length > 0 && items.every((b) => selezionate.has(b.id));
  const cambiaSelezione = (id) => setSelezionate((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
  }, []);

  // I file si salvano subito sul server, che li legge in coda da solo: aggiornare o chiudere la
  // pagina non perde nulla. Qui si fa solo l'invio dei file.
  const aggiungiFile = async (files) => {
    const nuovi = Array.from(files || []).filter((f) => /pdf|jpe?g|png/i.test(f.type));
    if (!nuovi.length) return;
    let errori = 0;
    setInvio({ fatti: 0, totale: nuovi.length, errori: 0 });
    for (let i = 0; i < nuovi.length; i += 1) {
      const fd = new FormData();
      fd.append('file', nuovi[i]);
      try {
        await axios.post(`${API}/luce-gas/bollette`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (e) {
        errori += 1;
        toast.error(`${nuovi[i].name}: ${e.response?.data?.detail || 'caricamento non riuscito'}`);
      }
      setInvio({ fatti: i + 1, totale: nuovi.length, errori });
      if (i === 0 || i === nuovi.length - 1) fetchItems();
    }
    setInvio(null);
    toast.success(`${nuovi.length - errori} bollette in coda di lettura`);
    fetchItems();
  };

  // aggiornamento automatico finché ci sono bollette in coda o in lettura
  const pendenti = items.filter((b) => ['in_coda', 'in_lettura'].includes(b.stato)).length;
  useEffect(() => {
    if (!pendenti) return undefined;
    const t = setInterval(() => {
      axios.get(`${API}/luce-gas/bollette`, { params: { search, tipo, stato, ordine, page_size: 200 } })
        .then((res) => { setItems(res.data.items); setTotal(res.data.total); }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [pendenti, search, tipo, stato, ordine]);

  const riprova = async (b) => {
    try {
      await axios.post(`${API}/luce-gas/bollette/${b.id}/riprova`);
      fetchItems();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const apri = async (b) => {
    if (inLavorazione(b)) return;
    setClientSearch('');
    setDettaglio({ bolletta: b, dati: { ...b.dati }, confermati: new Set(), fileUrl: '', isPdf: b.file.endsWith('.pdf') });
    try {
      const r = await axios.get(`${API}/luce-gas/bollette/${b.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      setDettaglio((d) => (d && d.bolletta.id === b.id ? { ...d, fileUrl: url } : d));
    } catch (_) { /* anteprima non essenziale */ }
  };

  const chiudi = () => {
    if (dettaglio?.fileUrl) URL.revokeObjectURL(dettaglio.fileUrl);
    setDettaglio(null);
  };

  const salva = async () => {
    setSalvando(true);
    try {
      const res = await axios.put(`${API}/luce-gas/bollette/${dettaglio.bolletta.id}`, {
        dati: dettaglio.dati, confermati: Array.from(dettaglio.confermati),
      });
      setDettaglio((d) => ({ ...d, bolletta: res.data, dati: { ...res.data.dati }, confermati: new Set() }));
      toast.success(res.data.avvisi.length ? `Salvato: restano ${res.data.avvisi.length} campi da verificare` : 'Salvato: bolletta verificata');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSalvando(false);
    }
  };

  const abbina = async (clientId) => {
    try {
      const res = await axios.post(`${API}/luce-gas/bollette/${dettaglio.bolletta.id}/abbina`, { client_id: clientId, crea_utenza: true });
      setDettaglio((d) => ({ ...d, bolletta: res.data }));
      toast.success(res.data.asset_id ? 'Abbinata: utenza collegata al cliente' : 'Abbinata al cliente');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const elimina = async () => {
    const id = dettaglio.bolletta.id;
    chiudi();
    await eliminaBollette([id]);
  };

  const setCampo = (k, tipoCampo) => (e) => {
    let v = e.target.value;
    if (tipoCampo === 'num') v = v === '' ? null : Number(String(v).replace(',', '.'));
    else if (tipoCampo !== 'date' && !NON_MAIUSCOLO.has(k)) v = v.toUpperCase();
    setDettaglio((d) => ({ ...d, dati: { ...d.dati, [k]: v } }));
  };

  const avvisiPer = (k) => (dettaglio?.bolletta.avvisi || []).filter((a) => a.campo === k);

  const filteredClients = clients.filter((c) => {
    if (!clientSearch) return false;
    const parole = clientSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const testo = `${c.last_name || ''} ${c.first_name || ''} ${c.company_name || ''} ${c.fiscal_code || ''} ${c.vat_number || ''}`.toLowerCase();
    return parole.every((p) => testo.includes(p));
  });


  return (
    <div className="space-y-6 animate-fade-in" data-testid="fatture-estrazione-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Fatture estrazione</h1>
        <p className="text-slate-600 mt-1">Bollette luce e gas: lettura automatica con l'AI locale, archivio e collegamento a cliente e utenza</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          {/* il trascinamento (anche di cartelle intere) lo gestisce components/TrascinaFile.js */}
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg py-6 text-yellow-800 hover:bg-yellow-100 cursor-pointer">
            <Upload className="w-6 h-6" />
            <span className="font-medium">Trascina qui le bollette o una cartella intera, oppure clicca per sceglierle</span>
            <span className="text-xs">PDF o foto, anche tante insieme (circa 20-30 secondi l'una)</span>
            <input type="file" multiple accept="application/pdf,image/jpeg,image/png" className="hidden"
              onChange={(e) => { aggiungiFile(e.target.files); e.target.value = ''; }} />
          </label>
          <div className="flex justify-center">
            <label className="text-sm text-yellow-800 hover:underline cursor-pointer">
              oppure scegli una cartella dal computer
              <input type="file" className="hidden" webkitdirectory="" directory="" multiple
                onChange={(e) => { aggiungiFile(e.target.files); e.target.value = ''; }} />
            </label>
          </div>
          {invio && (
            <div className="flex items-center gap-2 text-sm text-yellow-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento {invio.fatti} di {invio.totale}{invio.errori ? ` (${invio.errori} non riusciti)` : ''} — poi la lettura continua da sola sul server
            </div>
          )}
          {!invio && pendenti > 0 && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin text-yellow-600" />
              {pendenti} bollette in lettura sul server: puoi anche chiudere o aggiornare la pagina
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="relative lg:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca intestatario, POD/PDR, fornitore, offerta..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-2">
            {selezionate.size > 0 && (
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => eliminaBollette([...selezionate])}>
                <Trash2 className="w-4 h-4 mr-1" />Elimina selezionate ({selezionate.size})
              </Button>
            )}
            {[['', 'TUTTE'], ['luce', 'LUCE'], ['gas', 'GAS']].map(([k, l]) => (
              <Button key={k || 'tutte'} size="sm" variant={tipo === k ? 'default' : 'outline'} onClick={() => setTipo(k)}>{l}</Button>
            ))}
            <span className="w-px bg-slate-200 mx-1" />
            {[['', 'Tutti gli stati'], ['in_lavorazione', 'In lavorazione'], ['da_verificare', 'Da verificare'], ['da_abbinare', 'Da abbinare']].map(([k, l]) => (
              <Button key={k || 'tutti'} size="sm" variant={stato === k ? 'default' : 'outline'} onClick={() => setStato(k)}>{l}</Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="px-2 py-2 w-8">
                <input type="checkbox" checked={tutteSelezionate} title="Seleziona tutte"
                  onChange={() => setSelezionate(tutteSelezionate ? new Set() : new Set(items.map((b) => b.id)))} />
              </th>
              <th className="text-left px-2 py-2 font-medium cursor-pointer select-none hover:text-slate-900 whitespace-nowrap"
                onClick={() => setOrdine((o) => (o === 'desc' ? 'asc' : 'desc'))}
                title={ordine === 'desc' ? 'Più recenti in cima: clicca per invertire' : 'Più vecchie in cima: clicca per invertire'}>
                Caricata il {ordine === 'desc' ? '↓' : '↑'}
              </th>
              <th className="text-left px-2 py-2 font-medium">Emissione</th>
              <th className="text-left px-2 py-2 font-medium">Intestatario</th>
              <th className="text-left px-2 py-2 font-medium">POD / PDR</th>
              <th className="text-left px-2 py-2 font-medium">Fornitore / offerta</th>
              <th className="text-left px-2 py-2 font-medium">Periodo</th>
              <th className="text-right px-2 py-2 font-medium">Importo</th>
              <th className="text-right px-2 py-2 font-medium">Consumo<br />annuo</th>
              <th className="text-left px-2 py-2 font-medium">Scadenza<br />condizioni</th>
              <th className="text-left px-2 py-2 font-medium">Stato</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-slate-500">Caricamento...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-slate-500">Nessuna bolletta</td></tr>
            ) : items.map((b) => {
              const d = b.dati;
              const st = STATI[b.stato] || STATI.da_abbinare;
              return (
                <tr key={b.id} className={`border-t border-slate-900 hover:bg-slate-50 cursor-pointer ${selezionate.has(b.id) ? 'bg-amber-50' : ''}`} onClick={() => apri(b)}>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selezionate.has(b.id)} onChange={() => cambiaSelezione(b.id)} />
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-slate-600">
                    {new Date(b.created_at).toLocaleDateString('it-IT')}
                    <div>{new Date(b.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDate(d.data_emissione)}</td>
                  <td className="px-2 py-2">
                    {/* una riga per i nomi normali; i nomi molto lunghi (ditte) vanno a capo per stare nella pagina */}
                    <div className="font-medium text-slate-900 flex items-start gap-1 max-w-[280px] break-words">
                      {d.tipo_utenza === 'gas' ? <Flame className="w-3.5 h-3.5 mt-0.5 text-orange-500 flex-shrink-0" /> : <Zap className="w-3.5 h-3.5 mt-0.5 text-yellow-500 flex-shrink-0" />}
                      {inLavorazione(b) ? intestatario(b) : (
                        <button type="button"
                          className={`hover:underline text-left ${b.client_id ? 'hover:text-blue-700' : 'hover:text-sky-700'}`}
                          title={b.client_id ? 'Anteprima anagrafica del cliente' : 'Cliente non in anagrafica: clicca per registrarlo'}
                          onClick={(e) => { e.stopPropagation(); apriCliente(b); }}>
                          {intestatario(b)}
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">{d.partita_iva || d.codice_fiscale}</div>
                  </td>
                  {/* clic neutro: si può selezionare e copiare il codice senza aprire la scheda */}
                  <td className="px-2 py-2 font-mono text-xs cursor-text select-all" onClick={(e) => e.stopPropagation()}>{d.pod || d.pdr || '-'}</td>
                  <td className="px-2 py-2 max-w-[220px]">
                    <div className="text-xs font-medium">{d.fornitore || '-'}</div>
                    <div className="text-xs text-slate-500 uppercase">{d.nome_offerta}{d.tipo_prezzo && d.tipo_prezzo !== 'non indicato' ? ` · ${d.tipo_prezzo}` : ''}</div>
                  </td>
                  <td className="px-2 py-2 text-xs whitespace-nowrap">{formatDate(d.periodo_da)}<div>{formatDate(d.periodo_a)}</div></td>
                  <td className="px-2 py-2 text-right font-semibold text-violet-700">{formatCurrency(d.importo_totale)}</td>
                  <td className="px-2 py-2 text-right text-xs whitespace-nowrap">{formatNum(d.consumo_annuo)} {d.unita_consumo}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDate(d.scadenza_condizioni)}</td>
                  <td className="px-2 py-2">
                    <Badge className={st.classe}>{st.label}</Badge>
                    {b.doppione_di && <div className="text-xs text-red-600">doppione</div>}
                    {b.stato === 'errore' && (
                      <div className="text-xs text-red-600 max-w-[180px]">
                        {b.errore} <button type="button" className="underline" onClick={(e) => { e.stopPropagation(); riprova(b); }}>Riprova</button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" title="Elimina" onClick={() => eliminaBollette([b.id])}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {total > items.length && <p className="px-4 py-2 text-xs text-slate-500">Mostrate {items.length} di {total}: usa la ricerca</p>}
      </div>

      <Dialog open={!!anteprima} onOpenChange={(o) => !o && setAnteprima(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {anteprima?.nuovo ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" />Cliente non in anagrafica</DialogTitle>
                <DialogDescription>
                  {intestatario(anteprima.bolletta)} non è registrato. Registralo ora: i dati della bolletta sono già inseriti,
                  e alla fine la bolletta viene abbinata e l'utenza aggiunta in anagrafica.
                </DialogDescription>
              </DialogHeader>
              {/* estrazione dal documento d'identità come in Dimissioni: completa nascita, indirizzo
                  ecc. (i dati già presi dalla bolletta restano) e allega il documento in anagrafica */}
              <NewClientForm
                estrazione
                prefill={prefillDaBolletta(anteprima.bolletta.dati)}
                onCancel={() => setAnteprima(null)}
                onCreated={clienteCreato}
              />
            </>
          ) : anteprima?.caricamento ? (
            <div className="py-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento anagrafica...</div>
          ) : anteprima?.cliente && (() => {
            const c = anteprima.cliente;
            const riga = (et, v) => (v ? <div className="flex gap-3 text-sm"><span className="text-slate-500 w-28 flex-shrink-0">{et}</span><span className="text-slate-900 break-all">{v}</span></div> : null);
            const indirizzo = [[c.address, c.address_number].filter(Boolean).join(' '), [c.postal_code, c.city, c.city_provincia ? `(${c.city_provincia})` : ''].filter(Boolean).join(' ')].filter(Boolean).join(', ');
            const nascita = c.birth_date ? `${formatDate(c.birth_date)}${c.birth_comune ? ` a ${c.birth_comune}${c.birth_provincia ? ` (${c.birth_provincia})` : ''}` : c.birth_country ? ` — ${c.birth_country}` : ''}` : '';
            const codice = anteprima.bolletta.dati.pod || anteprima.bolletta.dati.pdr;
            const beneBolletta = anteprima.beni.find((x) => (x.pod_pdr_codice || '').toUpperCase() === (codice || '').toUpperCase());
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-emerald-600" />{c.is_company ? c.company_name : `${c.last_name} ${c.first_name}`}</DialogTitle>
                  <DialogDescription>Anteprima dell'anagrafica{c.client_code ? ` · codice ${c.client_code}` : ''}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {c.is_company && riga('P.IVA', c.vat_number)}
                  {c.is_company && riga('Referente', `${c.last_name || ''} ${c.first_name || ''}`.trim())}
                  {riga('Codice fiscale', c.fiscal_code)}
                  {riga('Nascita', nascita)}
                  {riga('Indirizzo', indirizzo)}
                  {riga('Telefono', c.phone)}
                  {riga('Email', c.email)}
                  {riga('PEC', c.pec)}
                  {riga('Documenti', (c.documents || []).length ? `${(c.documents || []).length} in anagrafica` : 'nessuno')}
                </div>
                <div className="border-t pt-3">
                  <div className="text-sm font-medium text-slate-700 mb-2">Utenze ({anteprima.beni.length})</div>
                  {anteprima.beni.length === 0 && <p className="text-sm text-slate-500">Nessuna utenza registrata.</p>}
                  {anteprima.beni.map((u) => (
                    <div key={u.id} className={`flex justify-between gap-2 text-sm py-1 ${u.id === beneBolletta?.id ? 'font-semibold text-amber-800' : ''}`}>
                      <span className="uppercase">{u.tipo_utenza} <span className="font-mono">{u.pod_pdr_codice}</span></span>
                      <span className="text-slate-600">{u.fornitore}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { const b = anteprima.bolletta; setAnteprima(null); apri(b); }}>Scheda bolletta</Button>
                  <Button onClick={() => navigate(`/clients?open=${c.id}${beneBolletta ? `&asset=${beneBolletta.id}` : ''}`)}>
                    <ExternalLink className="w-4 h-4 mr-1" />Apri anagrafica completa
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dettaglio} onOpenChange={(o) => !o && chiudi()}>
        <DialogContent className="max-w-7xl h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{dettaglio && `${dettaglio.dati.tipo_utenza === 'gas' ? 'GAS' : 'LUCE'} — ${intestatario(dettaglio.bolletta)}`}</DialogTitle>
            <DialogDescription>
              {dettaglio && (dettaglio.bolletta.avvisi.length
                ? `${dettaglio.bolletta.avvisi.length} campi da verificare (in giallo): correggili o conferma che sono giusti, poi salva.`
                : 'Nessun campo da verificare.')}
            </DialogDescription>
          </DialogHeader>
          {dettaglio && (
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="overflow-y-auto pr-2 space-y-4">
                {/* Abbinamento */}
                <div className={`p-3 rounded-lg border text-sm ${dettaglio.bolletta.client_id ? 'border-emerald-300 bg-emerald-50' : 'border-sky-300 bg-sky-50'}`}>
                  {dettaglio.bolletta.client_id ? (
                    <div className="flex items-center gap-2 text-emerald-800">
                      <UserCheck className="w-4 h-4" />
                      Cliente: <b>{dettaglio.bolletta.client_name}</b>
                      {dettaglio.bolletta.asset_id ? ' · utenza collegata' : (
                        <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => abbina(dettaglio.bolletta.client_id)}>Aggiungi utenza</Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sky-800">Nessun cliente trovato con questo codice fiscale / P.IVA / POD-PDR.</p>
                      <div className="relative">
                        <Input placeholder="Cerca il cliente da abbinare..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="bg-white" />
                        {filteredClients.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                            {filteredClients.slice(0, 8).map((c) => (
                              <div key={c.id} className="p-2 cursor-pointer hover:bg-slate-50 border-b last:border-b-0" onClick={() => abbina(c.id)}>
                                <div className="font-medium">{c.company_name || `${c.last_name} ${c.first_name}`}</div>
                                <div className="text-xs text-slate-500 font-mono">{c.vat_number || c.fiscal_code}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">Cliente nuovo? Registralo con <Link to="/nuova-da-bolletta" className="underline">Nuova da bolletta</Link>, poi torna qui.</p>
                    </div>
                  )}
                </div>

                {(dettaglio.bolletta.correzioni_regole || []).length > 0 && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                    <div className="font-semibold mb-1">Valori corretti con le regole fisse (letti esattamente dal testo della bolletta):</div>
                    {dettaglio.bolletta.correzioni_regole.filter((c) => c.campo !== 'mesi').map((c) => (
                      <div key={c.campo}>{ETICHETTE[c.campo] || c.campo}: {String(c.ai ?? '—')} → <b>{String(c.regola ?? '—')}</b></div>
                    ))}
                  </div>
                )}
                {GRUPPI.map(([titolo, campi]) => (
                  <div key={titolo}>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">{titolo}</h3>
                    <div className="grid grid-cols-6 gap-2">
                      {campi.filter(([k]) => !((dettaglio.dati.mesi || []).length && CAMPI_MENSILI.includes(k))).map(([k, label, tipoCampo, larghezza]) => {
                        const avv = avvisiPer(k);
                        const confermato = dettaglio.confermati.has(k);
                        return (
                          <div key={k} className={`space-y-1 ${tipoCampo === 'largo' ? 'col-span-6' : larghezza === 'terzo' ? 'col-span-2' : 'col-span-3'}`}>
                            <Label className="text-xs text-slate-500">{label}</Label>
                            <Input
                              type={tipoCampo === 'date' ? 'date' : 'text'}
                              value={dettaglio.dati[k] === null || dettaglio.dati[k] === undefined ? '' : String(dettaglio.dati[k])}
                              onChange={setCampo(k, tipoCampo)}
                              className={`h-8 text-sm ${tipoCampo !== 'date' ? 'uppercase' : ''} ${avv.length && !confermato ? 'border-amber-400 bg-amber-50' : ''}`}
                            />
                            {avv.length > 0 && (
                              <label className="flex items-center gap-1 text-xs text-amber-700">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />{avv[0].motivo}
                                <input type="checkbox" className="ml-auto" checked={confermato}
                                  onChange={(e) => setDettaglio((d) => {
                                    const s = new Set(d.confermati);
                                    if (e.target.checked) s.add(k); else s.delete(k);
                                    return { ...d, confermati: s };
                                  })} />
                                <span>giusto</span>
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {(dettaglio.dati.mesi || []).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">
                      Valori mese per mese {dettaglio.dati.mesi.length > 1 ? `(${dettaglio.dati.mesi.length} mesi, ognuno separato)` : ''}
                    </h3>
                    {dettaglio.dati.mesi.map((m, i) => (
                      <div key={m.mese} className="mb-3 rounded-md border border-slate-200 p-2">
                        <div className="text-sm font-semibold text-slate-800 mb-2">{MESI_NOMI[Number(m.mese.slice(5, 7))]} {m.mese.slice(0, 4)}</div>
                        <div className="grid grid-cols-3 gap-2">
                          {CAMPI_MESE.filter(([k]) => m[k] !== undefined && m[k] !== null).map(([k, label]) => (
                            <div key={k} className="space-y-1">
                              <Label className="text-xs text-slate-500">{label}</Label>
                              <Input value={String(m[k])} className="h-8 text-sm"
                                onChange={(e) => {
                                  const testo = e.target.value.replace(',', '.');
                                  setDettaglio((d) => {
                                    // testo così com'è mentre si scrive (il server lo legge come numero)
                                    const mesi = d.dati.mesi.map((x, j) => (j === i ? { ...x, [k]: testo === '' ? null : testo } : x));
                                    return { ...d, dati: { ...d.dati, mesi } };
                                  });
                                }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between pt-2 pb-4">
                  <Button variant="ghost" className="text-red-600" onClick={elimina}><Trash2 className="w-4 h-4 mr-1" />Elimina</Button>
                  <div className="flex gap-2">
                    {dettaglio.bolletta.client_id && (
                      <Button type="button" variant="outline" onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/luce-gas/pratiche/da-bolletta`, { bolletta_id: dettaglio.bolletta.id });
                          navigate(`/luce-gas/pratiche?apri=${r.data.id}`);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
                      }}>Crea pratica</Button>
                    )}
                    <Button onClick={salva} disabled={salvando}>{salvando ? 'Salvataggio...' : 'Salva correzioni'}</Button>
                  </div>
                </div>
              </div>
              <div className="min-h-0 bg-slate-100 rounded-lg overflow-hidden">
                {!dettaglio.fileUrl ? (
                  <div className="h-full flex items-center justify-center text-slate-500 text-sm">Caricamento bolletta...</div>
                ) : dettaglio.isPdf ? (
                  <iframe title="Bolletta" src={dettaglio.fileUrl} className="w-full h-full border-0" />
                ) : (
                  <img src={dettaglio.fileUrl} alt="Bolletta" className="w-full h-full object-contain" />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FattureEstrazione;
